import React, { useState, useEffect } from "react";
import DashboardLayout from "../layouts/Dashboardlayout";
import bg from "../../src/assets/svg/bg.svg";
import gradient from "../../src/assets/svg/gradient.svg";
import { AnimatedRobot } from "../components/ui/loader/AnimatedRobot";
import { Textarea } from "../components/ui/loader/TextArea";
import OpenAI from "openai";
import undo from "../../src/assets/svg/undo.svg";
import CodeViewer from "../components/CodeViewer";
import quillbot from "../assets/svg/quillbot.svg";
import send from "../assets/svg/send.svg";
import copy from "../assets/svg/copy.svg";

export default function MentorAI() {
  const initialState = {
    loading: false,
    results: null,
    inputValue: "",
    language: "",
    showInputs: false,
    currentStep: "language",
  };

  const [state, setState] = useState(initialState);
  const { loading, results, inputValue, language, showInputs, currentStep } =
    state;

  const isFormValid = inputValue.trim() && language.trim();

  const openAi = new OpenAI({
    apiKey: process.env.REACT_APP_OPENAI_API_KEY,
    dangerouslyAllowBrowser: true,
  });

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    setState((prev) => ({
      ...prev,
      showInputs: true,
    }));
  };

  const handleInputSubmit = (text) => {
    if (!text.trim()) return;

    if (currentStep === "language") {
      setState((prev) => ({
        ...prev,
        language: text,
        loading: true, // Start loading immediately
      }));

      // Call handleSubmit directly after setting language
      setTimeout(() => {
        handleSubmit(text);
      }, 0);
    }
  };

  const handleSubmit = async (languageText = "") => {
    // Use either the state language or the newly input language
    const finalLanguage = languageText || language;

    try {
      const result = await teacherHelper(inputValue, finalLanguage);
      setState((prev) => ({
        ...prev,
        results: result.content,
        language: finalLanguage, // Make sure to update the language in state
      }));
    } catch (error) {
      setState((prev) => ({
        ...prev,
        results: "An error occurred. Please try again.",
      }));
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleUndo = () => {
    setState(initialState);
  };

  const handleInputChange = (field) => (e) => {
    setState((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const teacherHelper = async (inputValue, language) => {
    const responseGPT = await openAi.chat.completions.create({
      model: "gpt-3.5-turbo",
      temperature: 0,
      max_tokens: 1000,
      messages: [
        {
          role: "system",
          content: `You are 'Teacher AI', a friendly and knowledgeable coding assistant.
    - Your name is Teacher AI, and you are here to help users learn how to code in various programming languages, including Python, JavaScript, Java, C++, and more.
    - You are multilingual and can explain coding concepts in the user's preferred language.
    - You provide step-by-step guidance, code examples, explanations of coding concepts, and best practices.
    - **ALWAYS** think step by step before returning a response.
    - You encourage users to ask questions and seek clarification when they don't understand something.
    - Keep responses concise, clear, and user-friendly; avoid overly technical jargon unless necessary for understanding.
    - Provide real-world examples and analogies to make coding concepts easier to grasp.
    - Use formatting to enhance readability: *text* for bold, _text_ for italics, '-' for bullet points.
    - Avoid unnecessary off-topic conversations; focus on helping users with coding-related queries.
    - If a topic is outside the scope of coding education, politely redirect the user back to coding-related topics.
    - Be patient and supportive, offering positive reinforcement to encourage continuous learning.`,
        },
        {
          role: "user",
          content: `I need assistance with a coding task. Here are the details of my request:
  I have a question about ${language}: ${inputValue} 
      
   Could you please provide a detailed explanation, including examples if necessary, to help me understand ${inputValue} better? only related to the ${language} so you are giving me ${inputValue} strictly based on the ${language}

      \`\`\`
          ${inputValue}
      \`\`\`

      Please review the code and let me know how I can improve it or fix any issues.`,
        },
      ],
    });

    return responseGPT.choices[0].message;
  };

  useEffect(() => {
    // Prevent ResizeObserver loop limit exceeded error
    const resizeObserverError = (error) => {
      if (error.message.includes("ResizeObserver")) {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div"
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay"
        );
        if (resizeObserverErr) {
          resizeObserverErr.style.display = "none";
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.style.display = "none";
        }
      }
    };

    window.addEventListener("error", resizeObserverError);

    return () => {
      window.removeEventListener("error", resizeObserverError);
    };
  }, []);

  if (showInputs) {
    return (
      <DashboardLayout>
        <div className="relative flex flex-col items-center justify-center w-full h-screen bg-center bg-cover">
          <img className="absolute top-0 right-0" src={gradient} />
          <div className="relative flex w-full h-full ">
            <div className="w-[40%] h-full lg:flex hidden flex-col justify-between gap-6 relative  px-6 pb-[230px] pt-6">
              <div className="flex flex-col gap-6 overflow-y-auto">
                <div className="flex justify-end w-full ">
                  <div className=" w-[479px] border border-white/10 rounded-[12px] bg-[#3F3F46] p-4">
                    <p className="text-white">{inputValue}</p>
                  </div>
                </div>

                {(currentStep === "language" || language) && (
                  <>
                    <div className="flex items-center gap-2 ">
                      <div className="w-[56px] h-[56px] quillbot rounded-full flex justify-center items-center">
                        <div className="w-[35px] h-[35px] rounded-full flex justify-center items-center bg-[white]/20 border border-[white]/10 drop-shadow-[0px_1.5px_4px_rgba(16,25,40,0.07)] backdrop-blur-[20px]">
                          <img
                            src={quillbot}
                            className="w-6 h-6"
                            alt="quillbot"
                          />
                        </div>
                      </div>
                      <h4 className="text-white text-[18px]">
                        Enter Language:
                      </h4>
                    </div>
                    {language && (
                      <div className="flex justify-end">
                        <div className="overflow-hidden w-[479px] border border-white/10 rounded-[12px] bg-[#3F3F46] p-4">
                          <p className="text-white">{language}</p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="absolute bottom-0 right-0 w-full p-6 bg-[#ffffff0b] backdrop-blur-[12px]">
                <div className="relative w-full h-full">
                  <div className="w-full md:h-[195px] h-[108px] rounded-[12px] bgt p-5">
                    <Textarea
                      placeholder={`Enter ${currentStep} here...`}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleInputSubmit(e.target.value);
                          e.target.value = "";
                        }
                      }}
                      disabled={loading || results}
                    />
                  </div>

                  <div
                    onClick={(e) => {
                      if (loading || results) return;
                      const textarea =
                        e.currentTarget.parentElement.querySelector("textarea");
                      if (textarea.value.trim()) {
                        handleInputSubmit(textarea.value);
                        textarea.value = "";
                      }
                    }}
                    className={`cursor-pointer absolute bottom-5 right-5 w-12 h-12 rounded-full flex justify-center items-center border border-white/10 bg-white/10 shadow-[inset_0px_8px_12px_rgba(255,255,255,0.4),inset_0px_20px_30px_rgba(255,255,255,0.4),inset_0px_-10px_75px_rgba(255,255,255,0.2)] backdrop-blur-[30px] ${
                      loading || results ? "opacity-50 cursor-not-allowed" : ""
                    }`}
                  >
                    <img src={send} className="w-6 h-6" alt="send" />
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-[60%] w-full bgtt rounded-[12px]  my-6 mr-6 ">
              <div className="w-full h-[72px] bg-[#27272A] rounded-t-[12px] border border-[#27272A] flex justify-between items-center px-6">
                <p className="text-white text-[20px]">Result</p>
                <div className="flex items-center gap-2">
                  <div className="cursor-pointer w-10 h-10 rounded-[8px] bg-[#ffffff0f] border border-[#ffffff14] flex justify-center items-center">
                    <img src={copy} className="w-6 h-6" alt="quillbot" />
                  </div>
                </div>
              </div>

              {loading ? (
                <div className="flex flex-col items-center justify-center h-full gap-6 text-white">
                  <AnimatedRobot className="md:w-[240px] w-[200px] md:h-[140px] h-[100px]" />
                  <p className="md:text-lg text-sm text-[#F2F4F7] font-medium text-center">
                    Debugging the bugs... and maybe questioning their life{" "}
                    <br />
                    choices 🐞. Hang tight, we're almost there!
                  </p>
                </div>
              ) : results ? (
                <div className="h-[calc(100vh-200px)] overflow-auto pt-10">
                  <CodeViewer code={results} language={language} />
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-white/60">Results will appear here...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="relative flex flex-col w-full bg-center bg-cover lg:h-screen md:justify-center md:items-center">
        {" "}
        <img className="absolute top-0 right-0" src={gradient} />
        <div className="">
          <div className="flex flex-col items-center justify-center gap-4 px-4 text-center md:px-0">
            <div className="w-[100px] h-[100px] quillbot rounded-full flex justify-center items-center">
              <div className="w-16 h-16 rounded-full flex justify-center items-center bg-[white]/20 border border-[white]/10 drop-shadow-[0px_1.5px_4px_rgba(16,25,40,0.07)] backdrop-blur-[20px]">
                <img src={quillbot} className="w-10 h-10" alt="quillbot" />
              </div>
            </div>
            <h2 className="md:text-[48px] text-[36px] font-semibold bg md:leading-[60px] leading-[40px] mt-6">
              What do you <br className="block md:hidden" />
              want to learn?
            </h2>
            <p className="text-[#F2F4F7] mt-4">
              Prompt, run, edit, and Learn about codes
            </p>
          </div>

          <div className="relative w-full mt-10">
            <div className="lg:w-[936px] w-full h-full relative ">
              <div className="w-full md:h-[195px] h-[108px] rounded-[12px] bgt p-5 ">
                <Textarea
                  placeholder="Ask Mentor Model"
                  onChange={handleInputChange("inputValue")}
                  value={inputValue}
                />
              </div>

              <div
                onClick={handleSend}
                disabled={!isFormValid}
                class=" cursor-pointer absolute bottom-5 right-5 w-12 h-12 rounded-full flex justify-center items-center border border-white/10 bg-white/10 shadow-[inset_0px_8px_12px_rgba(255,255,255,0.4),inset_0px_20px_30px_rgba(255,255,255,0.4),inset_0px_-10px_75px_rgba(255,255,255,0.2)] backdrop-blur-[30px]"
              >
                <img src={send} className="w-6 h-6" alt="send" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
